import { Text } from "@solomon-apps/shared/src/components/ui/text";
import {
    NeobankContinuousProgramSim,
    NeobankDepositCampaignProgramSim,
    NeobankProgramsMobileSim,
    NeobankProgramsOutcomeSim,
    ProgramActivationSim,
    ProgramConfigurationSim,
    ProgramPoliciesSim,
    ProgramViewSim
} from "@solomon-apps/shared/src/sim";
import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";

/**
 * Design-QA showcase for the +New Program sim-export blocks (the standalone,
 * auto-playing widgets consumed from `@solomon-apps/shared/sim` — the same
 * entry the docs/wiki embed).
 *
 * Split into an INDEX of cards ({@link ProgramStepsShowcase}) + one route per
 * block ({@link ProgramShowcaseBlock}), so only ONE auto-playing block mounts
 * at a time. The old all-on-one-page layout jumped around as each block's
 * height changed mid-play, making it hard to actually watch a given block.
 * Served unprotected; renders inside yaas-client (so it does NOT prove the
 * standalone export — that stays the job of the standalone bundle render).
 */
interface ShowcaseBlock {
    /** URL slug — the `:blockId` route param. */
    id: string;
    /** Short badge label (step number / letter). */
    label: string;
    title: string;
    group: "step" | "neobank";
    /** Wide blocks (the full-page rail+detail outcome) need more than the
     *  default 3xl container, else the detail's right edge clips. */
    wide?: boolean;
    /** Rendered lazily so the block only mounts (and auto-plays) on its route.
     *  Each block passes `framed` — the sim wraps itself in its own fixed-height
     *  frame (it KNOWS its max height), so the page can't shift as the looping
     *  sim changes height (the build → fade → rebuild cycle otherwise oscillated
     *  the page and hijacked scroll). */
    render: () => React.ReactNode;
}

const BLOCKS: ShowcaseBlock[] = [
    {
        id: "configuration",
        label: "1",
        group: "step",
        title: "Configuration",
        render: () => <ProgramConfigurationSim framed />
    },
    {
        id: "policies",
        label: "2",
        group: "step",
        title: "Policies",
        render: () => <ProgramPoliciesSim framed />
    },
    {
        id: "viewing",
        label: "3",
        group: "step",
        title: "Viewing Program",
        render: () => <ProgramViewSim framed />
    },
    {
        id: "activating",
        label: "4",
        group: "step",
        title: "Activating Program",
        render: () => <ProgramActivationSim framed />
    },
    {
        id: "neobank-continuous",
        label: "A",
        group: "neobank",
        title: "Continuous Balance Rewards — configure + activate",
        render: () => <NeobankContinuousProgramSim framed />
    },
    {
        id: "neobank-deposit",
        label: "B",
        group: "neobank",
        title: "Deposit Campaign — configure + activate",
        render: () => <NeobankDepositCampaignProgramSim framed />
    },
    {
        id: "neobank-outcome",
        label: "→",
        group: "neobank",
        title: "Programs page — both Active",
        wide: true,
        render: () => <NeobankProgramsOutcomeSim framed />
    },
    {
        id: "neobank-mobile",
        label: "▢",
        group: "neobank",
        title: "Programs page (mobile) — select a program",
        render: () => (
            <NeobankProgramsMobileSim
                framed
                frameClassName="max-w-sm mx-auto"
            />
        )
    }
];

const STEP_BLOCKS = BLOCKS.filter((b) => b.group === "step");
const NEOBANK_BLOCKS = BLOCKS.filter((b) => b.group === "neobank");

/**
 * Index — a grid of cards, each linking to its own block route. No sim mounts
 * here, so the page is static (nothing jumps).
 */
export function ProgramStepsShowcase(): React.ReactElement {
    return (
        <div className="min-h-screen bg-surface-ground py-14">
            <div className="mx-auto max-w-3xl px-6">
                <Text variant="h1" className="text-foreground-base">
                    +New Program — sim blocks
                </Text>
                <Text variant="body" className="text-foreground-muted mt-2">
                    Standalone, auto-playing sim-export widgets from{" "}
                    <code className="font-numeric text-[13px]">
                        @solomon-apps/shared/sim
                    </code>{" "}
                    — the blocks the docs embed. Click one to view it on its
                    own.
                </Text>

                <CardSection
                    title="Step blocks"
                    blocks={STEP_BLOCKS}
                    className="mt-10"
                />
                <CardSection
                    title="Neobank scenario"
                    subtitle="A neobank configures two programs in PMP end-to-end, then the Programs page shows both Active."
                    blocks={NEOBANK_BLOCKS}
                    className="mt-14"
                />
            </div>
        </div>
    );
}

function CardSection({
    title,
    subtitle,
    blocks,
    className
}: {
    title: string;
    subtitle?: string;
    blocks: ShowcaseBlock[];
    className?: string;
}): React.ReactElement {
    return (
        <section className={className}>
            <Text variant="h2" className="text-foreground-base">
                {title}
            </Text>
            {subtitle ? (
                <Text variant="body" className="text-foreground-muted mt-2">
                    {subtitle}
                </Text>
            ) : null}
            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {blocks.map((block) => (
                    <Link
                        key={block.id}
                        to={`/showcase/programs/${block.id}`}
                        className="group flex items-center gap-3 border border-line-base bg-surface-ground px-4 py-4 transition-colors hover:bg-surface-sunken"
                    >
                        <span className="flex size-7 shrink-0 items-center justify-center border border-line-base text-foreground-muted">
                            <Text variant="label" render={<span />}>
                                {block.label}
                            </Text>
                        </span>
                        <Text
                            variant="small"
                            render={<span />}
                            className="text-foreground-base group-hover:text-ember"
                        >
                            {block.title}
                        </Text>
                    </Link>
                ))}
            </div>
        </section>
    );
}

/**
 * A single block on its own route (`/showcase/programs/:blockId`) — only this
 * sim mounts + auto-plays, so nothing else on the page shifts height. Unknown
 * ids redirect back to the index.
 */
export function ProgramShowcaseBlock(): React.ReactElement {
    const { blockId } = useParams<{ blockId: string }>();
    const block = BLOCKS.find((b) => b.id === blockId);
    if (!block) {
        return <Navigate to="/showcase/programs" replace />;
    }
    return (
        <div className="min-h-screen bg-surface-ground py-14">
            <div
                className={`mx-auto px-6 ${block.wide ? "max-w-6xl" : "max-w-3xl"}`}
            >
                <Link
                    to="/showcase/programs"
                    className="inline-flex items-center gap-1 text-foreground-muted transition-colors hover:text-foreground-base"
                >
                    <Text variant="small" render={<span />}>
                        ← All blocks
                    </Text>
                </Link>
                <Text variant="h1" className="text-foreground-base mt-4">
                    {block.title}
                </Text>
                {/* Each block renders with `framed` — the sim wraps ITSELF in a
                    fixed-height frame at its own max height, so the looping sim's
                    build → fade → rebuild cycle changes content INSIDE a stable
                    box instead of oscillating the page height (which hijacked
                    scroll). The consumer never passes a pixel height. */}
                <div className="mt-8">{block.render()}</div>
            </div>
        </div>
    );
}
